import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardShell from "../components/DashboardShell";
import AuthImage from "../components/AuthImage";
import BadgeList from "../components/BadgeList";
import PostComposer from "../components/PostComposer";
import PostCard from "../components/PostCard";
import Modal from "../components/Modal";
import ProfileHeader from "../components/ProfileHeader";
import ProfileMediaGallery from "../components/ProfileMediaGallery";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  Linkedin, Twitter, Facebook, Instagram, Github, Globe, MapPin, Mail, Phone,
  ShieldCheck, BookmarkCheck,
} from "lucide-react";

const SOCIAL_ICONS = {
  linkedin: Linkedin,
  twitter: Twitter,
  facebook: Facebook,
  instagram: Instagram,
  github: Github,
  website: Globe,
};

export default function Profile() {
  const params = useParams();
  const navigate = useNavigate();
  const { user: viewer } = useAuth();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");
  const [posts, setPosts] = useState([]);
  const [postsPrivate, setPostsPrivate] = useState(false);
  const [followersOpen, setFollowersOpen] = useState(null); // "followers" | "following" | null
  const [followersList, setFollowersList] = useState({ items: [], count: 0, private: false });
  const [catalog, setCatalog] = useState([]);

  const loadProfile = useCallback(async () => {
    setError("");
    setProfile(null);
    try {
      let res;
      if (params.handle) {
        res = await api.get(`/profile/by-handle/${params.handle}`);
      } else if (params.id) {
        // Try as user first, fallback to org
        try { res = await api.get(`/profile/user/${params.id}`); }
        catch { res = await api.get(`/profile/org/${params.id}`); }
      } else {
        res = await api.get(`/profile/user/${viewer.id}`);
      }
      setProfile(res.data);
    } catch (e) { setError(formatApiError(e)); }
  }, [params.handle, params.id, viewer?.id]);

  useEffect(() => { if (viewer?.id) loadProfile(); }, [loadProfile, viewer?.id]);

  useEffect(() => {
    if (!profile) return;
    const ttype = profile.type === "organization" ? "org" : "user";
    api.get(`/posts/by/${ttype}/${profile.id}`).then((r) => {
      setPosts(r.data.items || []);
      setPostsPrivate(r.data.private);
    });
  }, [profile]);

  useEffect(() => {
    api.get(`/badges/catalog`).then((r) => setCatalog(r.data.items || []));
  }, []);

  const openList = async (which) => {
    if (!profile) return;
    const ttype = profile.type === "organization" ? "org" : "user";
    const { data } = await api.get(`/profile/${ttype}/${profile.id}/${which}`);
    setFollowersList(data);
    setFollowersOpen(which);
  };

  const shareProfile = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch (_) {}
  };

  if (error) {
    return (
      <DashboardShell>
        <div className="csc-card p-8 text-center text-red-400" data-testid="profile-error">{error}</div>
      </DashboardShell>
    );
  }
  if (!profile) {
    return (
      <DashboardShell>
        <div className="csc-card p-8 font-mono-tech text-xs text-[#94a3b8] uppercase tracking-wider">Loading profile…</div>
      </DashboardShell>
    );
  }

  const displayName = profile.full_name || profile.organization_name;
  const isOrg = profile.type === "organization";
  const isSelf = profile.is_self;
  const isPrivate = profile.profile_visibility === "private";

  return (
    <DashboardShell>
      <div className="space-y-6" data-testid="profile-page">
        {/* Banner + avatar */}
        <ProfileHeader
          profile={profile}
          onShare={shareProfile}
          onFollowChange={() => loadProfile()}
        />

        {/* Stats bar */}
        <div className="csc-card flex items-center divide-x divide-[rgba(0,170,255,0.18)]" data-testid="profile-stats">
          <div className="flex-1 px-6 py-4 text-center">
            <div className="text-2xl font-semibold">{profile.stats?.posts ?? 0}</div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">Posts</div>
          </div>
          <button onClick={() => openList("followers")} className="flex-1 px-6 py-4 text-center hover:bg-[#00aaff]/5" data-testid="open-followers">
            <div className="text-2xl font-semibold">{profile.stats?.followers ?? 0}</div>
            <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">Followers</div>
          </button>
          {!isOrg && (
            <button onClick={() => openList("following")} className="flex-1 px-6 py-4 text-center hover:bg-[#00aaff]/5" data-testid="open-following">
              <div className="text-2xl font-semibold">{profile.stats?.following ?? 0}</div>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-[#94a3b8]">Following</div>
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
          <div className="space-y-5">
            <ProfileMediaGallery profileId={profile.id} />

            <section className="csc-card p-6" data-testid="profile-about">
              <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-2">About</h2>
              <p className="text-sm text-white whitespace-pre-wrap min-h-[40px]">
                {profile.bio || <span className="text-[#94a3b8] italic">No bio yet.</span>}
              </p>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                {!isOrg && profile.occupation && (
                  <div className="flex items-center gap-2 text-[#94a3b8]"><BookmarkCheck size={12} className="text-[#39ff14]" /> {profile.occupation}</div>
                )}
                {!isOrg && profile.organization_affiliation && (
                  <div className="flex items-center gap-2 text-[#94a3b8]">
                    <ShieldCheck size={12} className="text-[#00aaff]" />
                    <span className="truncate">{profile.organization_affiliation}</span>
                  </div>
                )}
                {isOrg && profile.sector && (
                  <div className="flex items-center gap-2 text-[#94a3b8]"><ShieldCheck size={12} className="text-[#00aaff]" /> {profile.sector}</div>
                )}
                {isOrg && profile.address && (
                  <div className="flex items-center gap-2 text-[#94a3b8]"><MapPin size={12} className="text-[#39ff14]" /> {profile.address}</div>
                )}
                {isOrg && profile.phone && (
                  <div className="flex items-center gap-2 text-[#94a3b8]"><Phone size={12} className="text-[#00aaff]" /> {profile.phone}</div>
                )}
              </div>

              {profile.social_links && Object.keys(profile.social_links).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="social-links">
                  {Object.entries(profile.social_links).map(([k, v]) => {
                    const Icon = SOCIAL_ICONS[k] || Globe;
                    return (
                      <a key={k} href={v} target="_blank" rel="noopener noreferrer" className="csc-pill">
                        <Icon size={12} className="mr-1.5 inline" /> {k}
                      </a>
                    );
                  })}
                </div>
              )}
            </section>

            {isSelf && !isOrg && (
              <PostComposer onPosted={(p) => setPosts((arr) => [p, ...arr])} />
            )}

            <section className="space-y-4" data-testid="profile-posts">
              <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8]">Posts</h2>
              {isPrivate && !isSelf ? (
                <div className="csc-card p-6 text-center text-[#94a3b8]">This profile is private. Follow to see posts.</div>
              ) : posts.length === 0 ? (
                <div className="csc-card p-6 text-center text-[#94a3b8]">No posts yet.</div>
              ) : (
                posts.map((p) => (
                  <PostCard key={p.id} post={p} onDeleted={(id) => setPosts((arr) => arr.filter((x) => x.id !== id))} />
                ))
              )}
            </section>
          </div>

          {!isOrg && (
            <aside className="space-y-5">
              <section className="csc-card p-5" data-testid="profile-badges">
                <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-3">Badges</h3>
                <BadgeList owned={profile.badges || []} catalog={catalog} />
              </section>
            </aside>
          )}
          {isOrg && (
            <aside className="space-y-5">
              <section className="csc-card p-5">
                <h3 className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-[#94a3b8] mb-3">Organization</h3>
                <div className="text-xs text-[#94a3b8] space-y-2">
                  {profile.team_size && <div>Team size: <span className="text-white">{profile.team_size}</span></div>}
                  {profile.country && <div>Country: <span className="text-white">{profile.country}</span></div>}
                  {profile.ref_code && (
                    <div>Invite code: <span className="font-mono-tech text-[#00aaff]">{profile.ref_code}</span></div>
                  )}
                </div>
              </section>
            </aside>
          )}
        </div>
      </div>

      <Modal open={!!followersOpen} onClose={() => setFollowersOpen(null)} className="max-w-md max-h-[80vh] flex flex-col" data-testid="follow-list-modal" ariaLabel="Follow list">
        <div className="px-5 py-4 border-b border-[rgba(0,170,255,0.18)] flex items-center justify-between">
          <h3 className="font-semibold capitalize">{followersOpen} · {followersList.count || 0}</h3>
          <button onClick={() => setFollowersOpen(null)} className="text-[#94a3b8] hover:text-white text-sm" data-testid="follow-list-close">Close</button>
        </div>
        {followersList.private ? (
          <div className="p-6 text-center text-[#94a3b8] text-sm">This list is private.</div>
        ) : (
          <div className="overflow-auto">
            {followersList.items.length === 0 ? (
              <div className="p-6 text-center text-[#94a3b8] text-sm">None yet.</div>
            ) : (
              followersList.items.map((it) => (
                <Link key={`${it.type}-${it.id}`} to={it.handle ? `/u/${it.handle}` : `/profile/${it.id}`} onClick={() => setFollowersOpen(null)} className="flex items-center gap-3 px-5 py-3 hover:bg-[#00aaff]/5 border-b border-[rgba(0,170,255,0.18)] last:border-none">
                  <div className="w-9 h-9 rounded-full overflow-hidden border border-[rgba(0,170,255,0.18)]">
                    <AuthImage src={it.avatar_url} alt="" className="w-full h-full object-cover" fallback={<div className="w-full h-full bg-[#0d1b2e]" />} />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{it.name}</div>
                    <div className="font-mono-tech text-[10px] text-[#94a3b8]">@{it.handle}</div>
                  </div>
                </Link>
              ))
            )}
          </div>
        )}
      </Modal>
    </DashboardShell>
  );
}
